import { useRef, useEffect } from 'react'
import { TrendingUp, Users, MousePointerClick, Globe } from 'lucide-react'
import { Chart, BarElement, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, BarController, LineController, ArcElement, DoughnutController } from 'chart.js'
Chart.register(BarElement, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, BarController, LineController, ArcElement, DoughnutController)

const TT = { backgroundColor:'rgba(8,11,20,.95)', titleColor:'#EFF2FB', bodyColor:'#8B96B3', borderColor:'rgba(99,102,241,.4)', borderWidth:1, padding:10, cornerRadius:8 }

const KPIS = [
  { label:'Page Views',      value:'1.24M', change:'+22.4%', color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', icon: TrendingUp, trendColor: '#4F46E5',  bar:88, variant: 'indigo' },
  { label:'Unique Visitors', value:'84.2K', change:'+11.1%', color: '#06B6D4', colorGlow: 'rgba(6,182,212,0.15)', icon: Users,      trendColor: '#06B6D4' ,      bar:72, variant: 'cyan' },
  { label:'Bounce Rate',     value:'31.6%', change:'-4.2%',  color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', icon: MousePointerClick, trendColor: '#10B981', bar:32, trendDir:'neutral', variant: 'green' },
  { label:'Avg. Session',    value:'4m 12s',change:'+0.8%',  color: '#A855F7', colorGlow: 'rgba(168,85,247,0.15)', icon: Globe,            trendColor: '#A855F7', bar:55, variant: 'violet' },
]

const CHANNELS = [
  { name:'Organic Search', sessions:'18,420', pct:100, color:'#4F46E5' },
  { name:'Direct',         sessions:'12,100', pct:65,  color:'#06B6D4' },
  { name:'Referral',       sessions:'8,350',  pct:45,  color:'#10B981' },
  { name:'Social Media',   sessions:'5,240',  pct:28,  color:'#A855F7' },
  { name:'Email',          sessions:'3,120',  pct:17,  color:'#F59E0B' },
]

export default function Analytics() {
  const funnelRef = useRef(null); const funnelInst = useRef(null)
  const donutRef  = useRef(null); const donutInst  = useRef(null)

  useEffect(() => {
    if (funnelRef.current) {
      funnelInst.current?.destroy()
      funnelInst.current = new Chart(funnelRef.current, {
        type: 'bar',
        data: {
          labels: ['Visitors','Leads','Prospects','Trials','Customers'],
          datasets: [{ data:[100000,42000,18400,7200,2800], backgroundColor:['rgba(79,70,229,.9)','rgba(99,102,241,.75)','rgba(129,140,248,.6)','rgba(165,180,252,.5)','rgba(199,210,254,.4)'], borderRadius:6, borderSkipped:false }],
        },
        options: { indexAxis:'y', responsive:true, maintainAspectRatio:false, plugins:{ legend:{ display:false }, tooltip:TT }, scales:{ x:{ ticks:{ color:'#4A5578', font:{size:11} }, grid:{ color:'rgba(255,255,255,.03)' } }, y:{ ticks:{ color:'#8B96B3', font:{size:12} }, grid:{ display:false } } } },
      })
    }
    if (donutRef.current) {
      donutInst.current?.destroy()
      donutInst.current = new Chart(donutRef.current, {
        type: 'doughnut',
        data: { labels:['Desktop','Mobile','Tablet'], datasets:[{ data:[54,38,8], backgroundColor:['#4F46E5','#06B6D4','#10B981'], borderColor:'transparent', hoverOffset:6 }] },
        options: { responsive:true, maintainAspectRatio:false, cutout:'72%', plugins:{ legend:{display:false}, tooltip:TT } },
      })
    }
    return () => { funnelInst.current?.destroy(); donutInst.current?.destroy() }
  }, [])

  return (
    <div className="dash-page page-in">
      <h1 className="page-title">Analytics</h1>

      <div className="stats-grid cols-4">
        {KPIS.map(k => (
          <div key={k.label} className={`stat-card stat-card--${k.variant}`}>
             <span style={{"position": "absolute", "top": "0px", "right": "0px", "width": "110px", "height": "110px", "border-radius": "50%", "background": k.colorGlow  , "filter": "blur(30px)", "transform": "translate(30%, -30%)", "pointer-events": "none"}}></span>
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: k.colorGlow, color: k.color, border: `1px solid ${k.colorGlow}` }}>
                <k.icon size={15} strokeWidth={1.8}/>
              </div>
              <span className={`stat-card-trend${k.trendDir === 'neutral' ? ' neutral' : ''}`} style={{ color: k.trendColor, background: `${k.trendColor}15`, border: `1px solid ${k.colorGlow}` }}>
                {k.change}
              </span>
            </div>
            <div className="stat-card-value">{k.value}</div>
            <div className="stat-card-label">{k.label}</div>
            <div style={{ "position": "absolute", "bottom": "0px", "left": "0px", "right": "0px", "height": "2px", background: `linear-gradient(90deg,${k.color}88,transparent)`, "border-radius": "0 0 var(--radius-lg) var(--radius-lg)" }}></div>
          </div>
        ))}
      </div>

      <div className="charts-row">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Conversion Funnel</div>
              <div className="card-subtitle">Visitor to customer pipeline</div>
            </div>
          </div>
          <div style={{ position:'relative', height:220 }}><canvas ref={funnelRef}/></div>
        </div>
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Device Breakdown</div>
              <div className="card-subtitle">Sessions by device type</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:'var(--space-lg)', alignItems:'center' }}>
            <div style={{ position:'relative', height:160, width:160, flexShrink:0 }}><canvas ref={donutRef}/></div>
            <div style={{ display:'flex', flexDirection:'column', gap:'var(--space-sm)' }}>
              {[{l:'Desktop',p:'54%',c:'#4F46E5'},{l:'Mobile',p:'38%',c:'#06B6D4'},{l:'Tablet',p:'8%',c:'#10B981'}].map(d => (
                <div key={d.l} style={{ display:'flex', alignItems:'center', gap:'var(--space-sm)' }}>
                  <span style={{ width:10, height:10, borderRadius:2, background:d.c, flexShrink:0 }}/>
                  <span style={{ flex:1, fontSize:'var(--fs-sm)', color:'var(--text2)' }}>{d.l}</span>
                  <span style={{ fontSize:'var(--fs-sm)', fontWeight:600, color:'var(--text1)' }}>{d.p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Traffic Channels</div></div>
        {CHANNELS.map(c => (
          <div key={c.name} style={{ marginBottom:'var(--space-sm)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5, fontSize:'var(--fs-sm)' }}>
              <span style={{ color:'var(--text2)' }}>{c.name}</span>
              <span style={{ color:'var(--text1)', fontFamily:'var(--font-mono)', fontSize:'var(--fs-xs)' }}>{c.sessions}</span>
            </div>
            <div style={{ height:4, background:'var(--bg-raised)', borderRadius:'var(--radius-full)', overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${c.pct}%`, background:c.color, borderRadius:'var(--radius-full)', transition:'width .8s ease' }}/>
            </div>
          </div>
        ))}
      </div>

      <style>{`.dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}`}</style>
    </div>
  )
}